import { useLang } from '../../i18n/LanguageContext';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getOrders, urlDocument } from '../../services/api';

export default function OrderHistory() {
    const { t } = useLang();
    useAuth();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchOrders();
    }, []);

    const fetchOrders = async () => {
        try {
            const data = await getOrders();
            console.log('Orders fetched:', data);
            setOrders(data);
            setError(null);
        } catch (err) {
            console.error('Error fetching orders:', err);
            console.error('Error response:', err.response);
            const errorMsg = err.response?.status === 401
                ? t('compte.commandes.nonAuthentifie')
                : err.message || t('compte.commandes.erreur');
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const badges = {
            paid: { text: t('compte.statut.paid'), class: 'bg-green-100 text-green-800 border-green-300', icon: '✅' },
            shipped: { text: t('compte.statut.shipped'), class: 'bg-blue-100 text-blue-800 border-blue-300', icon: '🚚' },
            delivered: { text: t('compte.statut.delivered'), class: 'bg-purple-100 text-purple-800 border-purple-300', icon: '📦' },
            cancelled: { text: t('compte.statut.cancelled'), class: 'bg-red-100 text-red-800 border-red-300', icon: '❌' }
        };
        const badge = badges[status] || { text: status, class: 'bg-gray-100 text-gray-800 border-gray-300', icon: '📋' };
        return (
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border-2 ${badge.class}`}>
                <span>{badge.icon}</span>
                <span>{badge.text}</span>
            </span>
        );
    };

    if (loading) {
        return <div className="text-center py-8 text-text-light">{t('compte.commandes.chargement')}</div>;
    }

    if (error) {
        return <div className="text-center py-8 text-red-500 font-medium">{error}</div>;
    }

    return (
        <div>
            <div className="bg-white rounded-2xl border-2 border-primary/20 p-6 md:p-8 shadow-soft mb-6">
                <h2 className="text-2xl font-bold text-text mb-6">📋 Mes Commandes</h2>

                {orders.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-text-light mb-6 text-lg">{t('compte.commandes.aucune')}</p>
                        <a href="/products" className="inline-block px-6 py-3 bg-primary hover:bg-primary-dark text-white font-semibold rounded-lg transition-all">{t('compte.commandes.decouvrir')}</a>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {orders.map((order) => {
                            let items = [];
                            try {
                                if (typeof order.items === 'string') {
                                    items = JSON.parse(order.items);
                                } else if (Array.isArray(order.items)) {
                                    items = order.items;
                                } else if (typeof order.items === 'object' && order.items !== null) {
                                    items = Object.values(order.items);
                                }
                            } catch (e) {
                                console.error('Error parsing items:', e);
                                items = [];
                            }

                            return (
                                <div key={order.id} className="bg-surface/50 border-2 border-primary/10 rounded-xl p-5 hover:border-primary/30 transition-all">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 pb-4 border-b border-primary/10">
                                        <div>
                                            <h3 className="text-lg font-bold text-text mb-1">Commande #{order.orderNumber}</h3>
                                            <p className="text-sm text-text-light">{formatDate(order.createdAt)}</p>
                                        </div>
                                        {getStatusBadge(order.status)}
                                    </div>

                                    <div className="mb-4">
                                        <h4 className="text-sm font-semibold text-text uppercase tracking-wide mb-3">{t('compte.commandes.articles')}</h4>
                                        <div className="space-y-2">
                                            {items.map((item, idx) => (
                                                <div key={idx} className="flex items-center gap-2 text-sm text-text-light">
                                                    <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0"></span>
                                                    <span>{item.name} (x{item.quantity || 1})</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="flex flex-col gap-4">
                                        <div className="flex justify-between items-center pt-4 border-t border-primary/10">
                                            <strong className="text-text">{t('compte.commandes.total')}</strong>
                                            <span className="text-2xl font-bold text-primary">{parseFloat(order.totalPrice).toFixed(2)}€</span>
                                        </div>

                                        <div className="flex flex-col sm:flex-row gap-3">
                                            {order.trackingUrl && (
                                                <div className="flex-1 bg-sky/10 border-2 border-sky rounded-lg p-4">
                                                    {order.trackingNumber && (
                                                        <p className="text-sm text-text mb-3">
                                                            <strong>{t('compte.commandes.suiviLabel')}</strong> <span className="font-mono text-primary">{order.trackingNumber}</span>
                                                        </p>
                                                    )}
                                                    <a
                                                        href={order.trackingUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center justify-center gap-3 w-full px-6 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg"
                                                    >
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                                        </svg>
                                                        <span>{t('compte.commandes.suivre')}</span>
                                                    </a>
                                                </div>
                                            )}

                                            {/* Facture émise par le site (depuis le 23/09/2026) ; à
                                                défaut, la facture Stripe des commandes antérieures. */}
                                            {(order.factureChemin || order.invoiceUrl) && (
                                                <div className="flex-1 bg-gray-50 border-2 border-gray-200 rounded-lg p-4 flex flex-col justify-end gap-2">
                                                    {order.invoiceNumber && (
                                                        <p className="text-xs text-text-light text-center m-0">
                                                            {order.invoiceNumber}
                                                        </p>
                                                    )}
                                                    <a
                                                        href={order.factureChemin ? urlDocument(order.factureChemin) : order.invoiceUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center justify-center gap-3 w-full px-6 py-3.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 font-bold rounded-lg transition-all shadow-sm hover:shadow-md"
                                                    >
                                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                        </svg>
                                                        <span>{t('compte.commandes.facture')}</span>
                                                    </a>
                                                    {order.noteDeCreditChemin && (
                                                        <a
                                                            href={urlDocument(order.noteDeCreditChemin)}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-center text-sm text-gray-600 underline"
                                                        >
                                                            {t('compte.commandes.noteCredit')} {order.creditNoteNumber}
                                                        </a>
                                                    )}
                                                </div>
                                            )}

                                            {/* Review Button */}
                                            <div className="flex-1 bg-amber-50 border-2 border-amber-200 rounded-lg p-4 flex flex-col justify-end">
                                                <a
                                                    href="/#reviews"
                                                    className="flex items-center justify-center gap-3 w-full px-6 py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg transition-all shadow-sm hover:shadow-md"
                                                >
                                                    <span className="text-xl">⭐</span>
                                                    <span>{t('compte.commandes.avis')}</span>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
